/**
 * @file AbondonResp.jsx
 * @description Main page component for the Abandon Response insight module.
 *              Fetches text and email abandon data on mount, applies client-side date filtering,
 *              and displays KPI cards, abandon % horizontal bar chart, abandonments-over-time
 *              composed chart, and a paginated detail table.
 *
 * @component AbandonResp
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, IconButton, Typography
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Bar, BarChart, CartesianGrid, Cell, ComposedChart,
  Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis
} from "recharts";
import {
  fetchAbandonText,
  fetchAbandonEmail,
  fetchAbandonConversations,
} from "../../../services/insightModule/insightService";
import DatePickerComponent from "../../../common/components/DatePickerComponent";
import ConversationDrillDownDialog from "../../../components/ConversationDrillDownDialog";
import { STRINGS } from "../../../common/constants/commonConstants";

// ── Constants ──────────────────────────────────────────────────────
const BLUE   = "#378ADD";
const PINK   = "#D4537E";
const AMBER  = "#BA7517";
const GREEN  = "#1D9E75";
const PURPLE = "#7F77DD";

// Stage key (sent to backend) → display label / color
const STAGE_LABELS = { "1": "After 1R", "2": "After 2R", "3": "After 3R" };
const STAGE_COLORS = { "1": BLUE,        "2": AMBER,      "3": PURPLE     };

// ── Helpers ────────────────────────────────────────────────────────
function parseLocalDate(val) {
  if (!val) return null;
  const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
}

function fmtDate(val) {
  const d = parseLocalDate(val);
  if (!d || isNaN(d)) return val || "";
  return d.toLocaleDateString("en-US", {
    month: "2-digit", day: "2-digit", year: "2-digit",
  });
}

function safeNum(v) {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

function fmt(n, dec = 2) {
  const num = Number(n);
  if (isNaN(num)) return "—";
  return num.toFixed(dec);
}

function filterByDate(rows, startDate, endDate) {
  const start = startDate
    ? new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0, 0)
    : null;
  const end = endDate
    ? new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59, 999)
    : null;

  return (rows || []).filter(row => {
    const d = parseLocalDate(row.ContactDate);
    if (!d || isNaN(d.getTime())) return false;
    if (start && d < start) return false;
    if (end   && d > end)   return false;
    return true;
  });
}

// ── Sub-components ─────────────────────────────────────────────────
function KpiCard({ label, value, color = BLUE, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        background: "#f7f7f7", borderRadius: 2,
        p: "10px 14px", minWidth: 0, flex: 1,
        cursor: onClick ? "pointer" : "default",
        transition: "background .15s",
        "&:hover": onClick ? { background: "#efefef" } : {},
      }}
    >
      <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 500, color, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

function ChartCard({ title, children, height = 200 }) {
  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px" }}>
      {title && (
        <Typography sx={{
          fontSize: 11, fontWeight: 500, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.05em", mb: 1,
        }}>
          {title}
        </Typography>
      )}
      <Box sx={{ width: "100%", height }}>{children}</Box>
    </Box>
  );
}

// ── Main Component ─────────────────────────────────────────────────
const AbandonResp = () => {
  const [startDate, setStartDate] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [endDate,   setEndDate]   = useState(() => new Date());
  const [channel,   setChannel]   = useState("Texts");
  const [allText,   setAllText]   = useState([]);
  const [allEmail,  setAllEmail]  = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [page,      setPage]      = useState(0);
  const [drill,     setDrill]     = useState(null);
  const rowsPerPage = 25;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [t, e] = await Promise.all([
        fetchAbandonText(),
        fetchAbandonEmail(),
      ]);
      setAllText(Array.isArray(t)  ? t : []);
      setAllEmail(Array.isArray(e) ? e : []);
    } catch (err) {
      console.error(err);
      setError("Failed to load abandon data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const data = useMemo(() => {
    const raw = channel === "Emails" ? allEmail : allText;
    return filterByDate(raw, startDate, endDate);
  }, [channel, allText, allEmail, startDate, endDate]);

  // ── KPIs ───────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const responses    = data.reduce((a,b) => a + safeNum(b.Responses), 0);
    const abandon1     = data.reduce((a,b) => a + safeNum(b.Abandon1),  0);
    const abandon2     = data.reduce((a,b) => a + safeNum(b.Abandon2),  0);
    const abandon3     = data.reduce((a,b) => a + safeNum(b.Abandon3),  0);
    const abandonTotal = abandon1 + abandon2 + abandon3;
    return {
      responses,
      abandon1, abandon2, abandon3, abandonTotal,
      abandonRate: responses > 0 ? abandonTotal / responses * 100 : 0,
      after1Pct:   responses > 0 ? abandon1     / responses * 100 : 0,
      after2Pct:   responses > 0 ? abandon2     / responses * 100 : 0,
      after3Pct:   responses > 0 ? abandon3     / responses * 100 : 0,
    };
  }, [data]);

  // ── Abandon % bar data ─────────────────────────────────────────
  const pctBarData = useMemo(() => [
    { label: "After 1R", stage: "1", pct: kpis.after1Pct, count: kpis.abandon1, color: BLUE   },
    { label: "After 2R", stage: "2", pct: kpis.after2Pct, count: kpis.abandon2, color: AMBER  },
    { label: "After 3R", stage: "3", pct: kpis.after3Pct, count: kpis.abandon3, color: PINK   },
  ], [kpis]);

  // ── Chart data by date ─────────────────────────────────────────
  const chartData = useMemo(() => {
    const map = {};
    data.forEach(r => {
      const d = parseLocalDate(r.ContactDate);
      if (!d || isNaN(d)) return;
      const key = d.getTime();
      if (!map[key]) map[key] = {
        _ts: key, _convDate: r.ContactDate,
        date: fmtDate(r.ContactDate),
        responses: 0, abandon1: 0, abandon2: 0, abandon3: 0,
      };
      map[key].responses += safeNum(r.Responses);
      map[key].abandon1  += safeNum(r.Abandon1);
      map[key].abandon2  += safeNum(r.Abandon2);
      map[key].abandon3  += safeNum(r.Abandon3);
    });
    return Object.values(map)
      .map(r => ({
        ...r,
        abandonRate: r.responses > 0
          ? (r.abandon1 + r.abandon2 + r.abandon3) / r.responses * 100
          : 0,
      }))
      .sort((a, b) => a._ts - b._ts);
  }, [data]);

  // ── Pagination ─────────────────────────────────────────────────
  const sortedData = useMemo(() =>
    [...data].sort((a, b) => {
      const da = parseLocalDate(a.ContactDate);
      const db = parseLocalDate(b.ContactDate);
      return (db?.getTime() || 0) - (da?.getTime() || 0);
    })
  , [data]);

  const pagedData  = useMemo(() =>
    sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  , [sortedData, page]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  // ── Drill-down ─────────────────────────────────────────────────
  // stage only       → all conversations w/ that abandonment stage in range
  // date only        → all abandoned conversations on that date
  // stage + date     → that stage on that single date
  const openDrill = useCallback(({ stage = null, date = null }) => {
    const stageColor = stage ? STAGE_COLORS[stage] : null;
    const stageLabel = stage ? STAGE_LABELS[stage] : null;

    const title =
      stage && date ? "Conversations by stage & date" :
      stage         ? `Conversations abandoned ${stageLabel}` :
                      `Abandoned conversations on ${fmtDate(date)}`;

    const dStart = date ? new Date(date) : startDate;
    const dEnd   = date ? new Date(date) : endDate;

    setDrill({
      title,
      metric: stage ? { label: "Stage", value: stageLabel, color: stageColor } : null,
      context: [
        date && { label: "Date", value: fmtDate(date) },
        !date && startDate && { label: "From", value: fmtDate(startDate) },
        !date && endDate   && { label: "To",   value: fmtDate(endDate) },
      ].filter(Boolean),
      channel,
      // The DB stores the stage as a digit ("1"/"2"/"3"), so include both
      // forms so the chip color works either way.
      emotionColors: {
        "1": BLUE,
        "2": AMBER,
        "3": PURPLE,
        "After 1R": BLUE,
        "After 2R": AMBER,
        "After 3R": PURPLE,
      },
      fetchData: () => fetchAbandonConversations({
        stage,
        startDate:   dStart ? dStart.toISOString() : null,
        endDate:     dEnd   ? dEnd.toISOString()   : null,
        channeltype: channel,
      }),
    });
  }, [startDate, endDate, channel]);

  const axisStyle    = { fontSize: 10, fill: "#888" };
  const tooltipStyle = { fontSize: 12 };

  // 40px drill icon column at the start
  const COLS    = "40px 100px 90px 100px 90px 90px 90px 80px 80px 80px";
  const HEADERS = [
    "", "Date", "Responses", "Abandon Rate",
    "After 1R %", "After 2R %", "After 3R %",
    "After 1R", "After 2R", "After 3R",
  ];

  return (
    <Box sx={{ p: 2 }}>

      {/* ── Filters ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2,
        alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <DatePickerComponent
          label={STRINGS.START_DATE}
          date={startDate}
          onDateChange={setStartDate}
          marginRight={0}
        />
        <DatePickerComponent
          label={STRINGS.END_DATE}
          date={endDate}
          onDateChange={d => { if (d && d >= startDate) setEndDate(d); }}
          marginRight={0}
        />
        <Box
          onClick={() => setPage(0)}
          sx={{
            px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
            cursor: "pointer", userSelect: "none",
            background: BLUE, color: "#fff", fontWeight: 500,
            alignSelf: "flex-end", mb: "1px",
            "&:hover": { background: "#2a6db5" },
          }}
        >
          Search
        </Box>

        {/* Channel toggle */}
        <Box sx={{
          display: "flex", ml: "auto",
          border: "0.5px solid", borderColor: "divider",
          borderRadius: "20px", overflow: "hidden",
          alignSelf: "flex-end",
        }}>
          {["Texts", "Emails"].map(ch => (
            <Box key={ch} onClick={() => { setChannel(ch); setPage(0); }} sx={{
              px: "14px", py: "5px", fontSize: 13,
              fontWeight:  channel === ch ? 500 : 400,
              cursor: "pointer", userSelect: "none",
              background:  channel === ch ? BLUE : "transparent",
              color:       channel === ch ? "#fff" : "text.secondary",
              transition:  "all .15s",
            }}>
              {ch}
            </Box>
          ))}
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
          <CircularProgress size={20} /> Loading…
        </Box>
      )}

      {!loading && data.length === 0 && !error && (
        <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
          No data for selected range.
        </Typography>
      )}

      {!loading && data.length > 0 && (
        <>
          {/* ── SUMMARY KPIs ──────────────────────────────────── */}
          <SectionTitle>Summary</SectionTitle>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
            <KpiCard label="Total responses"    value={kpis.responses.toLocaleString()}    color={BLUE}  />
            <KpiCard label="Total abandonments" value={kpis.abandonTotal.toLocaleString()} color={PINK}
              onClick={() => openDrill({})} />
            <KpiCard label="Abandon rate"       value={`${fmt(kpis.abandonRate)}%`}        color={PINK}
              onClick={() => openDrill({})} />
            <KpiCard label="Completion rate"
              value={`${fmt(100 - kpis.abandonRate)}%`}                                    color={GREEN}  />
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <KpiCard label="Abandon after 1R" value={kpis.abandon1.toLocaleString()} color={BLUE}
              onClick={() => openDrill({ stage: "1" })} />
            <KpiCard label="Abandon after 2R" value={kpis.abandon2.toLocaleString()} color={AMBER}
              onClick={() => openDrill({ stage: "2" })} />
            <KpiCard label="Abandon after 3R" value={kpis.abandon3.toLocaleString()} color={PURPLE}
              onClick={() => openDrill({ stage: "3" })} />
          </Box>

          {/* ── ABANDON % HORIZONTAL BAR (click bar → drill stage) ── */}
          <SectionTitle>Abandon % by response stage</SectionTitle>

          <ChartCard height={140}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pctBarData} layout="vertical"
                margin={{ top: 4, right: 60, bottom: 4, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.04)" horizontal={false} />
                <XAxis type="number" tick={axisStyle} domain={[0, 100]}
                  tickFormatter={v => `${v}%`} />
                <YAxis type="category" dataKey="label"
                  tick={{ fontSize: 11, fill: "#555" }} width={70} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v, n, p) => [
                    `${fmt(p.payload.pct)}% (${p.payload.count.toLocaleString()})`,
                    "Abandon"
                  ]}
                />
                <Bar dataKey="pct" name="%" radius={[0,4,4,0]} cursor="pointer">
                  {pctBarData.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={entry.color}
                      onClick={() => openDrill({ stage: entry.stage })}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* ── ABANDONMENTS OVER TIME (click point → drill date) ── */}
          <SectionTitle>Abandonments over time</SectionTitle>

          <ChartCard height={240}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                style={{ cursor: "pointer" }}
                onClick={(state) => {
                  const dt = state?.activePayload?.[0]?.payload?._convDate;
                  if (dt) openDrill({ date: dt });
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="date" tick={axisStyle} />
                <YAxis yAxisId="left"  tick={axisStyle} />
                <YAxis yAxisId="right" orientation="right"
                  tick={{ ...axisStyle, fill: AMBER }}
                  tickFormatter={v => `${v}%`} domain={[0, 100]} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="left" dataKey="abandon1" name="After 1R" stackId="a" fill={BLUE}   radius={[0,0,0,0]} />
                <Bar yAxisId="left" dataKey="abandon2" name="After 2R" stackId="a" fill={AMBER}  radius={[0,0,0,0]} />
                <Bar yAxisId="left" dataKey="abandon3" name="After 3R" stackId="a" fill={PINK}   radius={[3,3,0,0]} />
                <Line yAxisId="right" type="monotone" dataKey="abandonRate"
                  name="Abandon rate %" stroke={PURPLE} dot={{ r: 3 }} strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* ── DATA TABLE ────────────────────────────────────── */}
          <SectionTitle>Detail</SectionTitle>

          <Box sx={{
            border: "0.5px solid", borderColor: "divider",
            borderRadius: 2, overflow: "hidden",
          }}>
            {/* Header */}
            <Box sx={{
              display: "grid",
              gridTemplateColumns: COLS,
              background: "#f7f7f7",
              borderBottom: "0.5px solid", borderColor: "divider",
              px: 2, py: 1,
              fontSize: 11, fontWeight: 500,
              color: "text.secondary",
              textTransform: "uppercase", letterSpacing: "0.05em",
              alignItems: "center",
            }}>
              {HEADERS.map((h, i) => (
                <Box key={i} sx={{ textAlign: i <= 1 ? "left" : "right" }}>{h}</Box>
              ))}
            </Box>

            {/* Rows */}
            {pagedData.map((row, i) => {
              const a1 = safeNum(row.Abandon1);
              const a2 = safeNum(row.Abandon2);
              const a3 = safeNum(row.Abandon3);
              return (
                <Box key={i} sx={{
                  display: "grid",
                  gridTemplateColumns: COLS,
                  px: 2, py: "6px",
                  fontSize: 12,
                  borderBottom: "0.5px solid", borderColor: "divider",
                  background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                  "&:hover": { background: "rgba(55,138,221,0.06)" },
                  alignItems: "center",
                }}>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => openDrill({ date: row.ContactDate })}
                      sx={{ width: 24, height: 24 }}
                      title="View abandoned conversations on this date"
                    >
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                  <Box>{fmtDate(row.ContactDate)}</Box>
                  <Box sx={{ textAlign: "right" }}>
                    {safeNum(row.Responses).toLocaleString()}
                  </Box>
                  <Box sx={{ textAlign: "right", color: PINK, fontWeight: 500 }}>
                    {row.AbandonRate ?? `${fmt(
                      safeNum(row.Responses) > 0
                        ? (a1+a2+a3) / safeNum(row.Responses) * 100
                        : 0
                    )}%`}
                  </Box>
                  <Box sx={{ textAlign: "right", color: BLUE }}>
                    {row.After1R ?? `${fmt(
                      safeNum(row.Responses) > 0 ? a1 / safeNum(row.Responses) * 100 : 0
                    )}%`}
                  </Box>
                  <Box sx={{ textAlign: "right", color: AMBER }}>
                    {row.After2R ?? `${fmt(
                      safeNum(row.Responses) > 0 ? a2 / safeNum(row.Responses) * 100 : 0
                    )}%`}
                  </Box>
                  <Box sx={{ textAlign: "right", color: PURPLE }}>
                    {row.After3R ?? `${fmt(
                      safeNum(row.Responses) > 0 ? a3 / safeNum(row.Responses) * 100 : 0
                    )}%`}
                  </Box>
                  <Box
                    onClick={a1 > 0
                      ? () => openDrill({ stage: "1", date: row.ContactDate })
                      : undefined}
                    sx={{
                      textAlign: "right", color: BLUE, fontWeight: 500,
                      cursor: a1 > 0 ? "pointer" : "default",
                      "&:hover": a1 > 0 ? { textDecoration: "underline" } : {},
                    }}
                  >
                    {a1}
                  </Box>
                  <Box
                    onClick={a2 > 0
                      ? () => openDrill({ stage: "2", date: row.ContactDate })
                      : undefined}
                    sx={{
                      textAlign: "right", color: AMBER, fontWeight: 500,
                      cursor: a2 > 0 ? "pointer" : "default",
                      "&:hover": a2 > 0 ? { textDecoration: "underline" } : {},
                    }}
                  >
                    {a2}
                  </Box>
                  <Box
                    onClick={a3 > 0
                      ? () => openDrill({ stage: "3", date: row.ContactDate })
                      : undefined}
                    sx={{
                      textAlign: "right", color: PURPLE, fontWeight: 500,
                      cursor: a3 > 0 ? "pointer" : "default",
                      "&:hover": a3 > 0 ? { textDecoration: "underline" } : {},
                    }}
                  >
                    {a3}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{
              display: "flex", justifyContent: "flex-end",
              alignItems: "center", gap: 1, mt: 1, fontSize: 12,
              color: "text.secondary",
            }}>
              <Box
                onClick={() => setPage(p => Math.max(0, p - 1))}
                sx={{
                  px: "10px", py: "3px", borderRadius: "20px",
                  border: "0.5px solid", borderColor: "divider",
                  cursor: page === 0 ? "default" : "pointer",
                  opacity: page === 0 ? 0.4 : 1,
                  "&:hover": { borderColor: page === 0 ? "divider" : BLUE },
                }}
              >
                ‹
              </Box>
              <span>Page {page + 1} of {totalPages}</span>
              <Box
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                sx={{
                  px: "10px", py: "3px", borderRadius: "20px",
                  border: "0.5px solid", borderColor: "divider",
                  cursor: page >= totalPages - 1 ? "default" : "pointer",
                  opacity: page >= totalPages - 1 ? 0.4 : 1,
                  "&:hover": { borderColor: page >= totalPages - 1 ? "divider" : BLUE },
                }}
              >
                ›
              </Box>
            </Box>
          )}
        </>
      )}

      {/* ── Drill-down popup ───────────────────────────────────── */}
      {drill && (
        <ConversationDrillDownDialog
          open={!!drill}
          onClose={() => setDrill(null)}
          {...drill}
        />
      )}
    </Box>
  );
};

export default AbandonResp;